import React, { useCallback, useMemo, useRef } from 'react';
import cn from 'bem-cn';

import { PromptStatus } from '@ai-platform/utils';

import prompthubEllipse from '../../../../images/icons/prompthub-ellipse.svg';
import { RoutePaths } from '../../../../constants';
import { Button, Link, Video } from '../../../UI';
import { ScreenSize, useGetScreenSize, useLike, MetrikaGoal, useMetrika } from '../../../../hooks';
import { sanitize } from '../../../../utils/sanitize';
import { openAliceChat } from '../../../../utils/alice/alice';

import { usePromptCardVisibility } from '../../../../VisibilityTracker';
import { useParallax } from '../../useParallax';
import { Like } from '../../components/Like';
import { Status } from '../../components/Status';
import { PromptCardProps } from '../../PromptCard.types';

import './PromptCardNew.scss';
import { PromptCardNewMobile } from './PromptCard.mobile';

const b = cn('prompt-card-new');

export const PromptCard: React.FC<PromptCardProps> = props => {
    const {
        prompt,
        size = 'normal',
        className,
        withHover,
        showStatus = false,
        needMarkAsViewed,
        fromCollection,
        fromPopular,
    } = props;

    const {
        title,
        description,
        videoData: { url: videoUrl },
        imageData: { urlOptimized: imageUrl },
        categories,
    } = prompt;

    const videoRef = useRef<HTMLVideoElement>(null);
    const screenSize = useGetScreenSize();

    const sanitizedTitle = sanitize(title);
    const sanitizedDescription = sanitize(description);
    const authorImage = prompt.author?.image || prompthubEllipse;
    const mainColor = prompt.imageData?.colors?.MainColor;

    const isMobile = screenSize === ScreenSize.Mobile;
    const isPhotoRevival = categories?.includes('photo-revival');
    const isCharacters = categories?.includes('characters');
    const isFullImageCard = (!isCharacters && prompt.author?.isEditor) || isPhotoRevival;
    const isPublished = prompt.status === PromptStatus.Published;
    const needHover = withHover && !isMobile && screenSize !== ScreenSize.Tablet;

    const { likes, isLiked, toggleLike } = useLike({
        promptId: Number(prompt.id),
        initialLikes: prompt.likes || 0,
        initialIsLiked: prompt.isLiked || false,
    });

    const { panelRef, transformX, transformY } = useParallax(1);

    const { reachGoal } = useMetrika();

    usePromptCardVisibility(panelRef, { prompt, needMarkAsViewed, videoRef });

    const isAliceButtonVisible = isPublished && size !== 'small';

    const handleOpenAliceChat = useCallback(
        (event: React.MouseEvent) => {
            event.preventDefault();
            event.stopPropagation();

            openAliceChat(prompt?.id);
            reachGoal(MetrikaGoal.ClickoutChat);

            if (fromCollection) {
                reachGoal(MetrikaGoal.ClickoutChatFromCollection);
            }

            if (isCharacters) {
                reachGoal(MetrikaGoal.ClickoutChatFromCharacters);
            }
        },
        [prompt?.id, reachGoal, fromCollection, isCharacters],
    );

    const handleCardClick = useCallback(
        (event: React.MouseEvent) => {
            if (fromCollection || fromPopular) {
                // Для промптов в подборке и секции топа сразу ведем в Алису
                return handleOpenAliceChat(event);
            }

            if (isCharacters) {
                reachGoal(MetrikaGoal.ClickCharactersPrompt);
            }
        },
        [fromCollection, fromPopular, handleOpenAliceChat, isCharacters, reachGoal],
    );

    const parallaxStyle = {
        transform: `perspective(200px) rotateY(${transformX}deg) rotateX(${transformY}deg)`,
    };

    // Для editorial карточек масштабирование применяется к background
    // Для обычных карточек - ко всей карточке
    const cardParallaxStyle = needHover ? parallaxStyle : {};

    const bgImage = isFullImageCard ? `url(${imageUrl})` : '';
    const backgroundStyle = {
        '--bg-image': bgImage,
        '--image-main-color': isFullImageCard ? mainColor : '',
    } as React.CSSProperties;

    const promptLink = useMemo(() => {
        return [isPublished ? RoutePaths.Prompt : RoutePaths.NewPromptStatus, prompt.id].join('/');
    }, [prompt.id, isPublished]);

    const renderProfile = () => {
        if (authorImage) {
            return <img src={authorImage} alt={'Аватарка'} className={b('avatar')} />;
        }

        return <div />;
    };

    if (size !== 'small' && isMobile && isPublished) {
        return <PromptCardNewMobile prompt={prompt} needMarkAsViewed={needMarkAsViewed} />;
    }

    return (
        <Link
            to={promptLink}
            state={{ fromClient: true, fromCollection }}
            ref={panelRef}
            className={b(
                { size, 'full-image': isFullImageCard, 'with-hover': needHover, 'photo-revival': isPhotoRevival },
                [className],
            )}
            onClick={handleCardClick}
        >
            {needHover && <div className={b('hover-trigger')} />}
            <div style={cardParallaxStyle} className={b('parallax')}>
                <div className={b('background')} style={backgroundStyle}>
                    {isPhotoRevival && Boolean(videoUrl) && (
                        <Video
                            ref={videoRef}
                            className={b('video')}
                            src={videoUrl}
                            muted
                            loop
                            playsInline
                            preload="auto"
                        />
                    )}
                </div>
                <div className={b('content')}>
                    <div className={b('header')}>
                        {showStatus && prompt.status ? <Status status={prompt.status} /> : renderProfile()}
                        {(!showStatus || isPublished) && (
                            <Like
                                size={size}
                                count={likes}
                                isEditorial={isFullImageCard}
                                isLiked={isLiked}
                                onClick={toggleLike}
                            />
                        )}
                    </div>
                    <div className={b('body')}>
                        <div className={b('front-side')}>
                            {!isFullImageCard && (
                                <div className={b('image-block')}>
                                    <img src={imageUrl} alt={prompt.title} className={b('image')} />
                                </div>
                            )}
                            <div className={b('title')}>
                                <p
                                    dangerouslySetInnerHTML={{
                                        __html: sanitizedDescription || sanitizedTitle,
                                    }}
                                />
                            </div>
                        </div>
                        <div className={b('back-side')}>
                            <div className={b('alter-text')}>
                                <p dangerouslySetInnerHTML={{ __html: sanitizedDescription }} />
                            </div>
                        </div>
                    </div>
                    <div className={b('button-group')}>
                        {isAliceButtonVisible && (
                            <Button
                                variant="primary"
                                fullWidth={!isPhotoRevival}
                                className={b('alter-button')}
                                onClick={handleOpenAliceChat}
                            >
                                Попробовать в Алисе AI
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </Link>
    );
};
